import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import {
  DndContext,
  KeyboardSensor,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type {
  DragEndEvent,
  DragStartEvent,
  DraggableSyntheticListeners,
  UniqueIdentifier,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface SortableItemContextValue {
  listeners: DraggableSyntheticListeners | undefined
  isDragging: boolean
  disabled: boolean
}

const SortableItemContext = createContext<SortableItemContextValue>({
  listeners: undefined,
  isDragging: false,
  disabled: false,
})

export interface SortableProps<T>
  extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    'onDragStart' | 'onDragEnd'
  > {
  value: T[]
  onValueChange: (value: T[]) => void
  getItemValue: (item: T) => string
  strategy?: 'vertical' | 'horizontal' | 'grid'
  children: React.ReactNode
}

export function Sortable<T>({
  value,
  onValueChange,
  getItemValue,
  strategy = 'vertical',
  children,
  ...props
}: SortableProps<T>): JSX.Element {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent): void => {
      const { active, over } = event
      setActiveId(null)
      if (over == null || active.id === over.id) return
      const activeIndex = value.findIndex(
        item => getItemValue(item) === active.id,
      )
      const overIndex = value.findIndex(item => getItemValue(item) === over.id)
      if (activeIndex !== -1 && overIndex !== -1) {
        onValueChange(arrayMove(value, activeIndex, overIndex))
      }
    },
    [value, getItemValue, onValueChange],
  )

  const itemIds = useMemo(() => value.map(getItemValue), [value, getItemValue])
  const sortingStrategy =
    strategy === 'horizontal'
      ? horizontalListSortingStrategy
      : strategy === 'grid'
      ? rectSortingStrategy
      : verticalListSortingStrategy

  return (
    <DndContext
      sensors={sensors}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={(event: DragStartEvent) => setActiveId(event.active.id)}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <SortableContext
        items={itemIds}
        strategy={sortingStrategy}
      >
        <div
          data-slot='sortable'
          data-dragging={activeId != null}
          {...props}
        >
          {children}
        </div>
      </SortableContext>
    </DndContext>
  )
}

export interface SortableItemProps
  extends React.HTMLAttributes<HTMLDivElement> {
  value: string
  disabled?: boolean
}

export function SortableItem({
  value,
  disabled = false,
  style,
  children,
  ...props
}: SortableItemProps): JSX.Element {
  const {
    setNodeRef,
    transform,
    transition,
    attributes,
    listeners,
    isDragging,
  } = useSortable({ id: value, disabled })

  const context = useMemo(
    () => ({ listeners, isDragging, disabled }),
    [listeners, isDragging, disabled],
  )

  return (
    <SortableItemContext.Provider value={context}>
      <div
        ref={setNodeRef}
        data-slot='sortable-item'
        data-value={value}
        data-dragging={isDragging}
        style={{
          ...style,
          transition,
          transform: CSS.Transform.toString(transform),
          ...(isDragging
            ? { position: 'relative' as const, zIndex: 1, opacity: 0.6 }
            : null),
        }}
        {...attributes}
        {...props}
      >
        {children}
      </div>
    </SortableItemContext.Provider>
  )
}

export interface SortableItemHandleProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode
}

export const SortableItemHandle: React.FC<SortableItemHandleProps> = ({
  style,
  children,
  ...props
}) => {
  const { listeners, isDragging } = useContext(SortableItemContext)

  return (
    <div
      data-slot='sortable-item-handle'
      data-dragging={isDragging}
      {...listeners}
      style={{
        cursor: isDragging ? 'grabbing' : 'grab',
        touchAction: 'none',
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  )
}
